// Google-Maps-style store pin map. Built on Leaflet + OpenStreetMap tiles.
// Used inside the state drill-down modal to show every geocoded store pincode
// as a marker on a real, pannable, street-level zoomable map.
//
// Pins are grouped by pincode (because that's the geocode granularity we have
// today — per-address enrichment is a future task). The popup lists the stores
// in that pincode and offers a one-click "Open in Google Maps" deep-link so
// field reps can navigate directly.
import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import axios from "axios";
import { Loader2, MapPin, Navigation, ExternalLink, RefreshCw } from "lucide-react";

const BRAND = "#2EB4AC";
const BRAND_DARK = "#1f8079";
const UNTAPPED = "#9CA3AF";       // gray-400 — pure-CNA pincodes
const UNTAPPED_RING = "#6B7280";  // gray-500 — outer ring

// Darken a hex by mixing toward black. Used to derive a "ring" colour for
// brand-tinted pins so the marker stays legible against a light map.
const _darken = (hex, amt = 0.35) => {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const m = (c) => Math.round(c * (1 - amt));
  return `rgb(${m(r)}, ${m(g)}, ${m(b)})`;
};

// Pre-build a divIcon for each (colour, count-bucket) so we don't allocate
// per render. Keyed by both to keep the cache correct across brand switches.
const _iconCache = {};
const tierForPincode = (ca, activeColor) => {
  // Any CA in this pincode → tapped (colour = active brand or teal).
  // Zero CA + some CNA → untapped grey.
  if (ca > 0) return { color: activeColor, ring: _darken(activeColor, 0.35) };
  return { color: UNTAPPED, ring: UNTAPPED_RING };
};
const getStoreIcon = (count, ca, activeColor) => {
  const t = tierForPincode(ca, activeColor);
  const key = `${t.color}-${count > 99 ? "99+" : count}`;
  if (_iconCache[key]) return _iconCache[key];
  const size = count >= 50 ? 44 : count >= 20 ? 38 : count >= 5 ? 32 : 28;
  const label = count > 99 ? "99+" : String(count);
  const html = `
    <div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:${t.color};
      border:2.5px solid #ffffff;
      box-shadow:0 2px 6px rgba(0,0,0,0.25),0 0 0 2px ${t.ring}66;
      display:flex;align-items:center;justify-content:center;
      color:#ffffff;font-weight:800;font-size:${size >= 38 ? 13 : 11}px;
      font-family:system-ui,-apple-system,sans-serif;
      transform:translateY(-2px);
    ">${label}</div>`;
  _iconCache[key] = L.divIcon({
    html,
    className: "horizon-store-marker",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
  return _iconCache[key];
};

// Re-fit the map bounds whenever the pin set changes. Uses `padding` so pins
// near the edge aren't clipped, and a max zoom to avoid over-zooming when
// only one or two pins are returned.
const FitBoundsToPins = ({ pins }) => {
  const map = useMap();
  useEffect(() => {
    if (!pins || pins.length === 0) return;
    const latlngs = pins.map((p) => [p.lat, p.lng]);
    const bounds = L.latLngBounds(latlngs);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 11 });
  }, [pins, map]);
  return null;
};

const StorePinMap = ({ state, apiBase, enrichmentStatus, onEnrichmentRefresh, flagFilters, brandColor }) => {
  const [pinsData, setPinsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Serialise flag filters so useEffect only re-fires when their values change.
  const flagKey = JSON.stringify(flagFilters || {});

  useEffect(() => {
    if (!state || !apiBase) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = { ...(flagFilters || {}) };
    Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
    axios
      .get(`${apiBase}/offline-cleaned/state/${encodeURIComponent(state)}/store-pins`, { params })
      .then((r) => { if (!cancelled) setPinsData(r.data || { pins: [] }); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, apiBase, flagKey]);

  const pins = pinsData?.pins || [];
  const coverage = pinsData ? {
    mapped: pinsData.mapped_pincodes || 0,
    total: pinsData.total_pincodes || 0,
    unmapped: pinsData.unmapped_pincodes || 0,
  } : null;

  // Default to India centre (geographic centroid ~ 22.5N, 79E) so the tile
  // layer is positioned correctly during the first paint before bounds fit.
  const initialCenter = useMemo(() => {
    if (pins.length > 0) return [pins[0].lat, pins[0].lng];
    return [22.5, 79];
  }, [pins]);

  return (
    <div className="absolute inset-0 flex flex-col bg-[#eef3f7]" data-testid="store-pin-map">
      {/* Coverage banner — visible whenever we have un-geocoded pincodes */}
      {coverage && coverage.unmapped > 0 && (
        <div className="absolute top-3 left-3 right-3 z-[400] flex items-center gap-2 px-3 py-2 bg-white/95 backdrop-blur rounded-lg border border-amber-200 shadow-md text-xs" data-testid="pin-map-coverage-banner">
          <MapPin className="w-4 h-4 text-amber-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-gray-800">
              {coverage.mapped.toLocaleString('en-IN')} of {coverage.total.toLocaleString('en-IN')} pincodes mapped
              <span className="text-gray-400 font-normal ml-1.5">· {Math.round(coverage.mapped / Math.max(1, coverage.total) * 100)}% coverage</span>
            </div>
            {enrichmentStatus?.running ? (
              <div className="text-[10.5px] text-gray-500 mt-0.5">
                Geocoding via OpenStreetMap…
                <span className="font-semibold tabular-nums ml-1">{enrichmentStatus.done}/{enrichmentStatus.total}</span>
                <span className="text-gray-400 ml-1">({Math.round((enrichmentStatus.done / Math.max(1, enrichmentStatus.total)) * 100)}%)</span>
              </div>
            ) : (
              <div className="text-[10.5px] text-gray-500 mt-0.5">
                {coverage.unmapped.toLocaleString('en-IN')} pincodes still need geocoding (rate-limited at 1/sec by OSM).
              </div>
            )}
          </div>
          {onEnrichmentRefresh && (
            <button
              onClick={onEnrichmentRefresh}
              disabled={enrichmentStatus?.running}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold rounded-md transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: BRAND, color: "#ffffff" }}
              data-testid="pin-map-enrich-btn"
              title={enrichmentStatus?.running ? "Geocoding in progress" : "Start background geocoding"}
            >
              <RefreshCw className={`w-3 h-3 ${enrichmentStatus?.running ? "animate-spin" : ""}`} />
              {enrichmentStatus?.running ? "Running" : "Geocode now"}
            </button>
          )}
        </div>
      )}

      {loading && (
        <div className="absolute inset-0 z-[300] flex items-center justify-center bg-white/40 backdrop-blur-sm">
          <div className="inline-flex items-center gap-2 px-4 py-2.5 bg-white rounded-full shadow-md border border-gray-200 text-sm text-gray-700">
            <Loader2 className="w-4 h-4 animate-spin" style={{ color: BRAND }} />
            Loading store pins…
          </div>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 z-[300] flex items-center justify-center">
          <div className="px-4 py-3 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-700">
            Failed to load pins: {error}
          </div>
        </div>
      )}

      {!loading && !error && pins.length === 0 && coverage && coverage.total > 0 && (
        <div className="absolute inset-0 z-[200] flex items-center justify-center pointer-events-none">
          <div className="max-w-sm px-5 py-4 bg-white/95 backdrop-blur rounded-xl shadow-lg border border-gray-200 text-center pointer-events-auto">
            <MapPin className="w-6 h-6 mx-auto mb-2 text-gray-400" />
            <div className="text-sm font-semibold text-gray-800">No pincodes geocoded yet for {state}</div>
            <div className="text-[11.5px] text-gray-500 mt-1">
              Click <span className="font-semibold">Geocode now</span> above to start the OpenStreetMap enrichment.
              It runs in the background — markers will appear automatically.
            </div>
          </div>
        </div>
      )}

      <MapContainer
        center={initialCenter}
        zoom={6}
        scrollWheelZoom={true}
        style={{ width: "100%", height: "100%", zIndex: 0 }}
        data-testid="leaflet-map"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
        />
        <FitBoundsToPins pins={pins} />
        {pins.map((p) => {
          const caPct = p.count ? Math.round((p.ca / p.count) * 100) : 0;
          // Pin colour: teal by default (Wellversed parent brand); brand
          // colour when a brand filter is active. Untapped pincodes (ca=0)
          // always render grey regardless of active brand.
          const activeColor = brandColor || BRAND;
          return (
            <Marker
              key={p.pincode}
              position={[p.lat, p.lng]}
              icon={getStoreIcon(p.count, p.ca || 0, activeColor)}
            >
              <Popup minWidth={280} maxWidth={340}>
                <div className="text-xs" data-testid={`pin-popup-${p.pincode}`}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="font-mono text-[14px] font-bold text-gray-900 tabular-nums">{p.pincode}</div>
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded tabular-nums"
                      style={{
                        background: caPct >= 80 ? "#e6f7f6" : caPct >= 50 ? "#fffbeb" : "#fef2f2",
                        color: caPct >= 80 ? BRAND_DARK : caPct >= 50 ? "#b45309" : "#be123c",
                      }}
                    >{caPct}% contact</span>
                  </div>
                  {p.district && (
                    <div className="text-[10.5px] text-gray-500 mb-1.5">{p.district}</div>
                  )}
                  <div className="flex items-center gap-2 mb-2 text-[11px]">
                    <span><span className="font-bold text-gray-900 tabular-nums">{p.count}</span> stores</span>
                    <span className="text-gray-300">·</span>
                    <span style={{ color: BRAND_DARK }} className="font-semibold tabular-nums">CA {p.ca}</span>
                    <span className="text-gray-300">·</span>
                    <span className="text-amber-700 font-semibold tabular-nums">CNA {p.cna}</span>
                  </div>
                  {p.categories && p.categories.length > 0 && (
                    <div className="text-[10px] text-gray-500 mb-2 truncate">{p.categories.join(', ')}</div>
                  )}
                  {p.stores && p.stores.length > 0 && (
                    <div className="border-t border-gray-100 pt-2 max-h-[180px] overflow-y-auto pr-1">
                      {p.stores.slice(0, 20).map((s, i) => (
                        <div key={i} className="mb-1.5 pb-1.5 border-b border-gray-50 last:border-b-0 last:mb-0 last:pb-0">
                          <div className="flex items-center justify-between gap-1.5">
                            <div className="font-semibold text-[11.5px] text-gray-800 truncate flex-1" title={s.name}>{s.name || "(unnamed store)"}</div>
                            {s.status && (
                              <span
                                className="text-[8.5px] font-bold px-1 py-0.5 rounded shrink-0"
                                style={{
                                  background: s.status === "CA" ? "#e6f7f6" : "#fffbeb",
                                  color: s.status === "CA" ? BRAND_DARK : "#b45309",
                                }}
                              >{s.status}</span>
                            )}
                          </div>
                          {s.address && <div className="text-[10px] text-gray-500 leading-tight mt-0.5" title={s.address}>{s.address}</div>}
                          <div className="flex items-center gap-2 mt-1 text-[10px]">
                            {s.phone && <span className="text-gray-700 font-mono">{s.phone}</span>}
                            {s.category && <span className="text-gray-400">· {s.category}</span>}
                          </div>
                        </div>
                      ))}
                      {p.stores.length > 20 && (
                        <div className="text-center text-[10px] text-gray-400 pt-1">+{p.stores.length - 20} more stores</div>
                      )}
                    </div>
                  )}
                  <div className="border-t border-gray-100 pt-2 mt-2 flex items-center gap-2">
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-[10.5px] font-semibold rounded-md text-white transition-colors flex-1 justify-center"
                      style={{ background: BRAND }}
                      data-testid={`pin-popup-navigate-${p.pincode}`}
                    >
                      <Navigation className="w-3 h-3" />
                      Directions
                    </a>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((p.stores?.[0]?.name || '') + ' ' + p.pincode)}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-[10.5px] font-semibold rounded-md text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors"
                      title="Search this area in Google Maps"
                      data-testid={`pin-popup-search-${p.pincode}`}
                    >
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default StorePinMap;
